/**
 * Dashboard Component
 * Shows user's polls with statistics, inline editing, and modern aesthetic design
 */

import React, { useState, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
    HiOutlineChartBar,
    HiOutlineCollection,
    HiOutlineCheckCircle,
    HiOutlineExclamationCircle,
    HiOutlinePencil,
    HiOutlineCheck,
    HiOutlineX,
    HiOutlineEye,
    HiOutlinePlus,
    HiOutlineChevronLeft,
    HiOutlineChevronRight,
    HiOutlineShare,
    HiOutlineArchive
} from 'react-icons/hi'
import api from '../utils/api'
import useAuth from '../hooks/useAuth'
import logger from '../utils/logger'
import ActivityTicker from './ActivityTicker'
import '../styles/animations.css'; // Ensure animations are available
import './Dashboard.css';

// Skeleton components for loading state defined in SkeletonLoader.js
import SkeletonLoader from './SkeletonLoader'

// Format date to readable string
const formatDate = (dateString) => {
    if (!dateString) return null
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

// Check if poll is expired
const isExpired = (expiresAt) => {
    if (!expiresAt) return false
    return new Date(expiresAt) < new Date()
}

// Action Tooltip component
const ActionTooltip = ({ children, label }) => {
    const [showTooltip, setShowTooltip] = useState(false)

    return (
        <div
            className="action-tooltip-container"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {children}
            {showTooltip && <span className="action-tooltip">{label}</span>}
        </div>
    )
}

// Stagger animation variants for poll cards
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0.1
        }
    }
}

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, ease: 'easeOut' }
    },
    exit: { opacity: 0, y: -20 }
}

// Inline editable title component
const EditableTitle = ({ title, onSave, pollId }) => {
    const [isEditing, setIsEditing] = useState(false)
    const [editValue, setEditValue] = useState(title)
    const [isSaving, setIsSaving] = useState(false)

    const handleSave = async () => {
        if (editValue.trim() === title.trim()) {
            setIsEditing(false)
            return
        }

        setIsSaving(true)
        try {
            await onSave(pollId, editValue.trim())
            setIsEditing(false)
        } catch (error) {
            logger.error('Failed to update poll title', { error: error.message })
            setEditValue(title) // Reset on error
        } finally {
            setIsSaving(false)
        }
    }

    const handleCancel = () => {
        setEditValue(title)
        setIsEditing(false)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSave()
        } else if (e.key === 'Escape') {
            handleCancel()
        }
    }

    if (isEditing) {
        return (
            <div className="editable-title editing">
                <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    disabled={isSaving}
                    maxLength={150}
                    className="editable-title-input"
                />
                <div className="editable-title-actions">
                    <button
                        onClick={handleSave}
                        disabled={isSaving || !editValue.trim()}
                        className="edit-action-btn save"
                        title="Save"
                    >
                        <HiOutlineCheck />
                    </button>
                    <button
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="edit-action-btn cancel"
                        title="Cancel"
                    >
                        <HiOutlineX />
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="editable-title">
            <span className="editable-title-text">{title}</span>
            <button
                onClick={() => setIsEditing(true)}
                className="edit-action-btn edit"
                title="Edit title"
            >
                <HiOutlinePencil />
            </button>
        </div>
    )
}

// Date tooltip with status badge as trigger - uses portal for proper z-index
const StatusBadgeTooltip = ({ createdAt, expiresAt, archivedAt, status }) => {
    const [showTooltip, setShowTooltip] = useState(false)
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })
    const buttonRef = React.useRef(null)

    // Determine status: archived > expired > active
    const isArchived = status === 'archived'
    const expired = !isArchived && (status === 'expired' || isExpired(expiresAt))

    const handleToggle = (e) => {
        e.stopPropagation()
        setShowTooltip(!showTooltip)
    }

    const updatePosition = useCallback(() => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect()
            setTooltipPosition({
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX
            })
        }
    }, [])

    // Update position when showing tooltip
    useEffect(() => {
        if (showTooltip) {
            updatePosition()
        }
    }, [showTooltip, updatePosition])

    // Close tooltip when clicking outside
    useEffect(() => {
        if (!showTooltip) return
        const handleClickOutside = () => setShowTooltip(false)
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [showTooltip])

    // Determine badge class and content
    let badgeClass = 'active'
    let badgeContent = <><HiOutlineCheckCircle /> Active</>
    if (isArchived) {
        badgeClass = 'archived'
        badgeContent = <><HiOutlineArchive /> Archived</>
    } else if (expired) {
        badgeClass = 'expired'
        badgeContent = <><HiOutlineExclamationCircle /> Expired</>
    }

    return (
        <div className="date-tooltip-container">
            <button
                ref={buttonRef}
                className={`status-badge ${badgeClass}`}
                onClick={handleToggle}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                aria-label="View dates"
            >
                {badgeContent}
            </button>
            {showTooltip && ReactDOM.createPortal(
                <div
                    className="date-tooltip-portal"
                    style={{
                        position: 'absolute',
                        top: tooltipPosition.top,
                        left: tooltipPosition.left
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    <div className="date-tooltip-item">
                        <span className="date-tooltip-label">Created</span>
                        <span className="date-tooltip-value">{formatDate(createdAt)}</span>
                    </div>
                    {expiresAt && (
                        <div className="date-tooltip-item">
                            <span className="date-tooltip-label">{expired || isArchived ? 'Expired' : 'Expires'}</span>
                            <span className="date-tooltip-value">{formatDate(expiresAt)}</span>
                        </div>
                    )}
                    {archivedAt && (
                        <div className="date-tooltip-item">
                            <span className="date-tooltip-label">Archived</span>
                            <span className="date-tooltip-value">{formatDate(archivedAt)}</span>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </div>
    )
}

// Poll card component
const PollCard = ({ poll, onUpdateTitle }) => {
    const isArchived = poll.status === 'archived'
    const expired = poll.status === 'expired' || (!poll.status && isExpired(poll.expires_at))
    const [copied, setCopied] = useState(false)

    // Determine card class based on status
    let cardClass = 'active'
    if (isArchived) cardClass = 'archived'
    else if (expired) cardClass = 'expired'

    const handleShare = async () => {
        const pollUrl = `${window.location.origin}/poll/${poll.short_code}`
        try {
            await navigator.clipboard.writeText(pollUrl)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea')
            textArea.value = pollUrl
            document.body.appendChild(textArea)
            textArea.select()
            document.execCommand('copy')
            document.body.removeChild(textArea)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    return (
        <motion.div
            className={`dashboard-poll-card ${cardClass}`}
            variants={cardVariants}
            layout
        >
            {/* Status badge with date tooltip */}
            <StatusBadgeTooltip
                createdAt={poll.created_at}
                expiresAt={poll.expires_at}
                archivedAt={poll.archived_at}
                status={poll.status}
            />

            {/* Title - centered, editable on hover */}
            <div className="poll-card-title-area">
                <EditableTitle
                    title={poll.title}
                    pollId={poll.id}
                    onSave={onUpdateTitle}
                />
            </div>

            {/* Bottom row: votes on left, actions on right */}
            <div className="poll-card-footer">
                <div className="poll-card-votes">
                    <HiOutlineChartBar />
                    <span className="vote-count">{poll.total_votes}</span>
                    <span className="vote-label">votes</span>
                </div>

                <div className="poll-card-actions">
                    {copied && (
                        <span className="copied-label">Link copied</span>
                    )}
                    <ActionTooltip label={copied ? 'Copied!' : 'Share poll'}>
                        <button
                            className={`poll-action-icon ${copied ? 'copied' : ''}`}
                            onClick={handleShare}
                            title={copied ? 'Copied!' : 'Share poll'}
                        >
                            {copied ? <HiOutlineCheck /> : <HiOutlineShare />}
                        </button>
                    </ActionTooltip>
                    <ActionTooltip label="View poll">
                        <Link
                            to={`/poll/${poll.short_code}`}
                            className="poll-action-icon"
                            title="View poll"
                        >
                            <HiOutlineEye />
                        </Link>
                    </ActionTooltip>
                </div>
            </div>
        </motion.div>
    )
}

// Empty state component with decorative rings
const EmptyState = () => (
    <motion.div
        className="dashboard-empty-state"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
    >
        <div className="empty-state-icon-wrapper">
            <div className="empty-state-ring" />
            <div className="empty-state-ring" />
            <div className="empty-state-ring" />
            <div className="empty-state-icon">
                <HiOutlineCollection />
            </div>
        </div>
        <h3>No polls yet</h3>
        <p>Create your first poll and start collecting votes!</p>
        <Link to="/" className="empty-state-cta">
            <HiOutlinePlus /> Create Poll
        </Link>
    </motion.div>
)

// Main Dashboard component
function Dashboard() {
    const { user, profile } = useAuth()
    const [polls, setPolls] = useState([])
    const [stats, setStats] = useState({ totalPolls: 0, totalVotes: 0, trends: { votes24h: 0 } })
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1 })
    const [loading, setLoading] = useState(true)
    const [paginationLoading, setPaginationLoading] = useState(false)
    const [itemsPerPageLoading, setItemsPerPageLoading] = useState(false)
    const [loadingDirection, setLoadingDirection] = useState(null) // 'prev' or 'next'
    const [error, setError] = useState(null)

    // Activity feed state
    const [activity, setActivity] = useState([])
    const [loadingActivity, setLoadingActivity] = useState(true)

    // Filter and sort state
    const [filter, setFilter] = useState(() => {
        return localStorage.getItem('dashboard_filter') || 'all'
    })
    const [sortBy, setSortBy] = useState(() => {
        return localStorage.getItem('dashboard_sort') || 'newest'
    })

    // Pagination configuration state
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        const saved = localStorage.getItem('dashboard_items_per_page')
        return saved ? parseInt(saved) : 9
    })

    // Persist filter/sort/pagination preferences
    useEffect(() => {
        localStorage.setItem('dashboard_filter', filter)
    }, [filter])

    useEffect(() => {
        localStorage.setItem('dashboard_sort', sortBy)
    }, [sortBy])

    useEffect(() => {
        localStorage.setItem('dashboard_items_per_page', itemsPerPage.toString())
    }, [itemsPerPage])

    // Compute active polls count from fetched data
    const activePolls = polls.filter(poll => poll.status === 'active' || (!poll.status && !isExpired(poll.expires_at))).length

    // Sort polls (filtering is now done server-side via status parameter)
    const filteredPolls = polls
        .sort((a, b) => {
            if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at)
            if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at)
            if (sortBy === 'mostVotes') return b.total_votes - a.total_votes
            return 0
        })

    const fetchPolls = useCallback(async (page = 1, limit = null, isPagination = false, isItemsPerPageChange = false, statusOverride = null) => {
        // Use passed limit, or fall back to current itemsPerPage from state
        const actualLimit = limit !== null ? limit : itemsPerPage
        // Map filter to API status parameter
        const statusParam = statusOverride || (filter === 'all' ? 'visible' : filter)
        try {
            if (isPagination) {
                setPaginationLoading(true)
            } else if (isItemsPerPageChange) {
                setItemsPerPageLoading(true)
            } else {
                setLoading(true)
            }
            setError(null)

            let response;
            if (user) {
                response = await api.getUserPolls(page, actualLimit, statusParam)
            } else {
                response = await api.getGuestPolls(page, actualLimit)
            }


            setPolls(response.polls || [])
            setStats(response.stats || { totalPolls: 0, totalVotes: 0, archivedPolls: 0 })
            setPagination({
                page: response.page,
                totalPages: response.totalPages
            })
        } catch (err) {
            logger.error('Failed to fetch polls', { error: err.message })
            setError('Failed to load your polls. Please try again.')
        } finally {
            setLoading(false)
            setPaginationLoading(false)
            setItemsPerPageLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, filter, itemsPerPage])

    useEffect(() => {
        fetchPolls()
    }, [fetchPolls])

    // Fetch activity feed
    useEffect(() => {
        const fetchActivity = async () => {
            try {
                const data = await api.getUserActivity()
                setActivity(data.activities || [])
            } catch (err) {
                logger.error('Failed to fetch activity', { error: err.message })
            } finally {
                setLoadingActivity(false)
            }
        }
        if (user) {
            fetchActivity()
        }
    }, [user])

    const handleUpdateTitle = async (pollId, newTitle) => {
        await api.updatePollTitle(pollId, newTitle)
        // Update local state
        setPolls(prevPolls =>
            prevPolls.map(poll =>
                poll.id === pollId ? { ...poll, title: newTitle } : poll
            )
        )
    }

    const handlePageChange = (newPage, direction) => {
        if (newPage >= 1 && newPage <= pagination.totalPages && !paginationLoading) {
            setLoadingDirection(direction)
            fetchPolls(newPage, null, true)
        }
    }

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage)
        // Clear polls immediately so skeleton shows while loading
        setPolls([])
        // Reset to page 1 when changing items per page, pass new limit directly
        fetchPolls(1, newItemsPerPage, false, true)
    }

    // Loading state
    if (loading && polls.length === 0) {
        return <SkeletonLoader variant="dashboard" />
    }

    // Error state
    if (error) {
        return (
            <div className="dashboard">
                <div className="dashboard-header">
                    <h1>Your Dashboard</h1>
                </div>
                <div className="dashboard-error">
                    <HiOutlineExclamationCircle />
                    <p>{error}</p>
                    <button onClick={() => fetchPolls()} className="retry-btn">
                        Try Again
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="dashboard">
            {/* Activity Ticker - positioned at top, right below site header */}
            {user && <ActivityTicker activities={activity} loading={loadingActivity} />}

            <div className="dashboard-header">
                <h1>Your Dashboard</h1>
                <p className="dashboard-subtitle">
                    {user ? `Welcome back${profile?.display_name ? `, ${profile.display_name}` : ''}!` : 'Access your anonymously created polls here.'}
                </p>
            </div>

            {/* Stats Section */}
            <div className="dashboard-stats-container">
                <div className="dashboard-stats">
                    <motion.div
                        className="dashboard-stat-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <div className="stat-icon polls">
                            <HiOutlineCollection />
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{stats.totalPolls}</span>
                            <span className="stat-label">Total Polls</span>
                        </div>
                    </motion.div>

                    <motion.div
                        className="dashboard-stat-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                    >
                        {stats.trends?.votes24h > 0 && (
                            <span className="notification-pill positive">
                                +{stats.trends.votes24h} today
                            </span>
                        )}
                        <div className="stat-icon votes">
                            <HiOutlineChartBar />
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{stats.totalVotes}</span>
                            <span className="stat-label">Total Votes</span>
                        </div>
                    </motion.div>

                    <motion.div
                        className="dashboard-stat-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <div className="stat-icon active">
                            <HiOutlineCheckCircle />
                        </div>
                        <div className="stat-content">
                            <span className="stat-value">{activePolls}</span>
                            <span className="stat-label">Active</span>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Polls Section */}
            <div className="dashboard-section">
                <div className="section-header">
                    <h2>Your Polls</h2>

                </div>

                {polls.length === 0 && !itemsPerPageLoading ? (
                    <EmptyState />
                ) : (
                    <>
                        {/* Filter and Sort Controls */}
                        <div className="filter-sort-controls">
                            <div className="filter-pills">
                                <button
                                    className={`filter-pill ${filter === 'all' ? 'active' : ''}`}
                                    onClick={() => setFilter('all')}
                                >
                                    All
                                </button>
                                <button
                                    className={`filter-pill ${filter === 'active' ? 'active' : ''}`}
                                    onClick={() => setFilter('active')}
                                >
                                    Active
                                </button>
                                <button
                                    className={`filter-pill ${filter === 'expired' ? 'active' : ''}`}
                                    onClick={() => setFilter('expired')}
                                >
                                    Expired
                                </button>
                                <button
                                    className={`filter-pill ${filter === 'archived' ? 'active' : ''}`}
                                    onClick={() => setFilter('archived')}
                                >
                                    Archived {stats.archivedPolls > 0 && <span className="filter-count">({stats.archivedPolls})</span>}
                                </button>
                            </div>
                            <div className="controls-right">
                                <div className="pagination-selector">
                                    <label htmlFor="items-per-page-select">Per page:</label>
                                    <select
                                        id="items-per-page-select"
                                        value={itemsPerPage}
                                        onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                                    >
                                        <option value="9">9</option>
                                        <option value="18">18</option>
                                        <option value="36">36</option>
                                    </select>
                                </div>
                                <div className="sort-dropdown">
                                    <label htmlFor="sort-select">Sort:</label>
                                    <select
                                        id="sort-select"
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                    >
                                        <option value="newest">Newest</option>
                                        <option value="oldest">Oldest</option>
                                        <option value="mostVotes">Most Votes</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {itemsPerPageLoading ? (
                            <div className="dashboard-polls-grid">
                                {/* Show skeleton cards while loading new items per page */}
                                {Array.from({ length: Math.min(itemsPerPage, stats.totalPolls || itemsPerPage) }).map((_, index) => (
                                    <div key={index} className="dashboard-poll-card skeleton compact">
                                        {/* Status badge skeleton */}
                                        <div className="skeleton-badge status"></div>
                                        {/* Title skeleton - centered */}
                                        <div className="skeleton-title-area">
                                            <div className="skeleton-line title"></div>
                                        </div>
                                        {/* Footer skeleton */}
                                        <div className="skeleton-footer">
                                            <div className="skeleton-votes"></div>
                                            <div className="skeleton-actions">
                                                <div className="skeleton-action-btn"></div>
                                                <div className="skeleton-action-btn"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredPolls.length === 0 && !paginationLoading ? (
                            <div className="filter-no-results">
                                <HiOutlineCollection />
                                <p>No {filter} polls found</p>
                            </div>
                        ) : (
                            <motion.div
                                className="dashboard-polls-grid"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                key={`${filter}-${sortBy}-${pagination.page}-${itemsPerPage}`}
                            >
                                <AnimatePresence mode="popLayout">
                                    {filteredPolls.map(poll => (
                                        <PollCard
                                            key={poll.id}
                                            poll={poll}
                                            onUpdateTitle={handleUpdateTitle}
                                        />
                                    ))}
                                </AnimatePresence>
                            </motion.div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="dashboard-pagination">
                                <button
                                    onClick={() => handlePageChange(pagination.page - 1, 'prev')}
                                    disabled={pagination.page <= 1 || paginationLoading}
                                    className={`pagination-btn ${paginationLoading && loadingDirection === 'prev' ? 'btn-loading' : ''}`}
                                >
                                    <HiOutlineChevronLeft /> Previous
                                </button>
                                <span className="pagination-info">
                                    Page {pagination.page} of {pagination.totalPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(pagination.page + 1, 'next')}
                                    disabled={pagination.page >= pagination.totalPages || paginationLoading}
                                    className={`pagination-btn ${paginationLoading && loadingDirection === 'next' ? 'btn-loading' : ''}`}
                                >
                                    Next <HiOutlineChevronRight />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default Dashboard
